import React, { Fragment, useEffect, useState } from "react";
import { XIcon } from "../../icons";

import { Dialog, Transition } from "@headlessui/react";

type ModalProps = {
  open: boolean;
  onClose: (b: boolean) => void;
  children: React.ReactNode;
};
// headlessui can stall mid-transition and never unmount, trapping focus in an invisible dialog.
// Remove once a fix merges upstream, e.g. https://github.com/tailwindlabs/headlessui/pull/3906.
const LEAVE_TRANSITION_OVERRUN_MS = 600;

const Modal = ({ open, onClose, children }: ModalProps) => {
  const [transitionStalled, setTransitionStalled] = useState(false);

  useEffect(() => {
    if (open) {
      return;
    }

    const timer = setTimeout(() => setTransitionStalled(true), LEAVE_TRANSITION_OVERRUN_MS);
    return () => {
      clearTimeout(timer);
      setTransitionStalled(false);
    };
  }, [open]);

  if (!open && transitionStalled) {
    return null;
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all my-8 max-w-md w-full">
                <div
                  className="inline-block float-right mt-3 text-gray-300 hover:text-gray-400 hover:cursor-pointer"
                  onClick={() => onClose(false)}
                >
                  <XIcon className="h-6 w-6" />
                </div>
                <div className="p-2.5">{children}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Object.assign(Modal, { Title: Dialog.Title });
